/* statsKit.tsx — presentation primitives for the Statistics surfaces, ported
   from the Claude Design mock (Statistics.html / stats-kit.jsx).

   Like the Tips and Notifications subtabs, this carries its own dark palette as
   scoped CSS variables rather than the global --color-* tokens: the mock is a
   self-contained dark design and the app renders dark. */
import type { CSSProperties, ReactNode } from 'react';
import { FeatherIcon } from '../FeatherIcon';

export const STAT_BASE: CSSProperties = {
  ['--bg' as string]: 'var(--om-surface)',
  ['--card' as string]: 'var(--om-legacy-card)',
  ['--card-2' as string]: 'var(--om-card)',
  ['--line' as string]: 'color-mix(in srgb, var(--om-ink) 7.5%, transparent)',
  ['--line-2' as string]: 'color-mix(in srgb, var(--om-ink) 5%, transparent)',
  ['--text' as string]: 'var(--om-legacy-text)',
  ['--muted' as string]: 'var(--om-sub-2)',
  ['--faint' as string]: 'var(--om-dim)',
  ['--accent' as string]: 'var(--om-legacy-accent)',
  ['--accent-on' as string]: '#fff',
  ['--accent-ico' as string]: 'var(--om-legacy-pale)',
  ['--accent-soft' as string]: 'color-mix(in srgb, var(--om-accent) 16%, transparent)',
  ['--ok' as string]: '#3ddc84',
  ['--ok-ico' as string]: '#6ee7a5',
  ['--ok-soft' as string]: 'rgba(61,220,132,.14)',
  ['--ok-line' as string]: 'rgba(61,220,132,.28)',
  ['--warn' as string]: '#f0a868',
  ['--warn-soft' as string]: 'rgba(240,168,104,.14)',
  ['--bad' as string]: '#ff6b81',
  ['--input-bg' as string]: 'color-mix(in srgb, var(--om-ink) 3.5%, transparent)',
  ['--rad-lg' as string]: '20px',
  ['--rad-md' as string]: '14px',
  ['--mono' as string]: 'var(--font-mono)',
};

export type Tone = 'accent' | 'ok' | 'warn';

export const toneColor = (tone: Tone) =>
  tone === 'ok' ? 'var(--ok)' : tone === 'warn' ? 'var(--warn)' : 'var(--accent)';
const toneIcon = (tone: Tone) =>
  tone === 'ok' ? 'var(--ok-ico)' : tone === 'warn' ? 'var(--warn)' : 'var(--accent-ico)';
const toneSoft = (tone: Tone) =>
  tone === 'ok' ? 'var(--ok-soft)' : tone === 'warn' ? 'var(--warn-soft)' : 'var(--accent-soft)';

// ── formatting ───────────────────────────────────────────────────────
export const fmt = (n: number) => Math.round(n).toLocaleString('en-US');
export const usd = (n: number) =>
  '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Percentages read as "6.2%" when small and "43%" when not — one decimal is
    noise above 10 and hides a real difference below it. */
export const pct = (n: number) => (n < 10 ? n.toFixed(1) : String(Math.round(n))) + '%';
/** Every ratio on this page can divide by zero (a quiet account, an empty range). */
export const ratio = (a: number, b: number) => (b > 0 ? a / b : 0);

// ── card shell ───────────────────────────────────────────────────────
export function Card({
  icon,
  title,
  hint,
  tone = 'accent',
  right,
  children,
}: {
  icon?: string;
  title: string;
  hint?: string;
  tone?: Tone;
  right?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      style={{
        borderRadius: 'var(--rad-lg)',
        border: '1px solid var(--line)',
        background: 'var(--card)',
        padding: 22,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
        {icon && (
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 9,
              background: toneSoft(tone),
              color: toneIcon(tone),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FeatherIcon name={icon} size={16} />
          </span>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 15.5, fontWeight: 750, color: 'var(--text)' }}>{title}</h2>
          {hint && <p style={{ margin: 0, fontSize: 12.5, fontWeight: 500, color: 'var(--faint)' }}>{hint}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

// ── sparkline ────────────────────────────────────────────────────────
export function Spark({ data, color = 'var(--accent)', h = 34 }: { data: number[]; color?: string; h?: number }) {
  if (data.length === 0) return null;
  const max = Math.max(...data, 1);
  const w = 100;
  const step = w / Math.max(data.length - 1, 1);
  const pts = data.map((v, i) => [i * step, h - (v / max) * (h - 4) - 2] as const);
  const line = pts.map((p) => p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ');
  const last = pts[pts.length - 1] ?? ([0, h] as const);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height: h, display: 'block', overflow: 'visible' }}>
      <polygon points={`0,${h} ${line} ${w},${h}`} fill={color} opacity=".13" />
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <circle cx={last[0]} cy={last[1]} r="2.4" fill={color} />
    </svg>
  );
}

// ── hero KPI ─────────────────────────────────────────────────────────
export function Kpi({
  icon,
  label,
  value,
  sub,
  tone = 'accent',
  spark,
  sparkFrom,
  sparkTo,
  children,
}: {
  icon: string;
  label: string;
  value: string;
  sub?: string;
  tone?: Tone;
  spark?: number[];
  sparkFrom?: string;
  sparkTo?: string;
  children?: ReactNode;
}) {
  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 'var(--rad-lg)',
        border: '1px solid var(--line)',
        background: 'var(--card)',
        padding: '20px 22px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(120% 90% at 100% 0%, ${toneSoft(tone)}, transparent 60%)`,
          pointerEvents: 'none',
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, position: 'relative' }}>
        <span style={{ color: toneIcon(tone), display: 'flex' }}>
          <FeatherIcon name={icon} size={15} />
        </span>
        <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--muted)' }}>
          {label}
        </span>
      </div>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 44,
            lineHeight: 0.95,
            fontWeight: 800,
            letterSpacing: -1.4,
            color: 'var(--text)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </span>
        {sub && <span style={{ fontSize: 13.5, fontWeight: 650, color: 'var(--muted)' }}>{sub}</span>}
      </div>
      {children}
      {spark && spark.length > 1 && (
        <div style={{ position: 'relative', marginTop: 'auto' }}>
          <Spark data={spark} color={toneColor(tone)} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, fontWeight: 600, color: 'var(--faint)' }}>
            <span>{sparkFrom}</span>
            <span>{sparkTo}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── metric row with inline proportion bar ────────────────────────────
export function MetricRow({
  label,
  value,
  of,
  tone = 'accent',
  money,
  strong,
  note,
}: {
  label: string;
  value: number;
  /** When given, the row draws a bar showing `value` as a share of this. */
  of?: number;
  tone?: Tone;
  money?: boolean;
  strong?: boolean;
  note?: string;
}) {
  const zero = value === 0;
  const share = of !== undefined && of > 0 ? Math.min(100, (value / of) * 100) : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 0', borderTop: '1px solid var(--line-2)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <span
          style={{
            flex: 1,
            fontSize: 14,
            fontWeight: strong ? 700 : 550,
            color: zero ? 'var(--faint)' : strong ? 'var(--text)' : 'var(--muted)',
          }}
        >
          {label}
        </span>
        {note && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--faint)', fontFamily: 'var(--mono)' }}>{note}</span>}
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 15,
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            color: zero ? 'var(--faint)' : money ? 'var(--ok-ico)' : 'var(--text)',
          }}
        >
          {money ? usd(value) : fmt(value)}
        </span>
      </div>
      {share !== null && (
        <div style={{ height: 4, borderRadius: 4, background: 'color-mix(in srgb, var(--om-ink) 5%, transparent)', overflow: 'hidden' }}>
          <div style={{ width: share + '%', height: '100%', borderRadius: 4, background: toneColor(tone), opacity: zero ? 0 : 0.85 }} />
        </div>
      )}
    </div>
  );
}

// ── ranked bar list ──────────────────────────────────────────────────
export interface BarItem {
  label: string;
  n: number;
  icon?: string;
}

export function BarList({
  items,
  total,
  tone = 'warn',
  showIcons,
}: {
  items: BarItem[];
  total: number;
  tone?: Tone;
  showIcons?: boolean;
}) {
  const max = Math.max(...items.map((i) => i.n), 1);
  const c = toneColor(tone);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((it) => (
        <div
          key={it.label}
          style={{ display: 'grid', gridTemplateColumns: 'minmax(150px, 32%) 1fr 92px', alignItems: 'center', gap: 16 }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              fontSize: 13.5,
              fontWeight: 600,
              minWidth: 0,
              color: it.n === 0 ? 'var(--faint)' : 'var(--text)',
            }}
          >
            {showIcons && (
              <span style={{ color: 'var(--faint)', display: 'flex' }}>
                <FeatherIcon name={it.icon || 'alert-circle'} size={15} />
              </span>
            )}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.label}>
              {it.label}
            </span>
          </span>
          <span style={{ height: 10, borderRadius: 6, background: 'color-mix(in srgb, var(--om-ink) 4%, transparent)', overflow: 'hidden', display: 'block' }}>
            <span
              style={{
                display: 'block',
                height: '100%',
                width: Math.max((it.n / max) * 100, it.n ? 2 : 0) + '%',
                borderRadius: 6,
                background: `linear-gradient(90deg, ${c}, ${c} 70%, color-mix(in srgb, var(--om-ink) 35%, transparent))`,
                opacity: 0.9,
              }}
            />
          </span>
          <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 8 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 15, fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
              {fmt(it.n)}
            </span>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--faint)', fontFamily: 'var(--mono)', width: 32, textAlign: 'right' }}>
              {total > 0 ? Math.round((it.n / total) * 100) : 0}%
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

// ── empty state for all-zero blocks ──────────────────────────────────
export function ZeroState({ text, action, onAction }: { text: string; action?: string; onAction?: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px 12px',
        flexWrap: 'wrap',
        padding: '14px 16px',
        borderRadius: 'var(--rad-md)',
        background: 'var(--card-2)',
        border: '1px dashed var(--line)',
      }}
    >
      <span style={{ color: 'var(--faint)', display: 'flex' }}>
        <FeatherIcon name="info" size={16} />
      </span>
      <span style={{ flex: '1 1 220px', minWidth: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--muted)', lineHeight: 1.45 }}>
        {text}
      </span>
      {action && onAction && (
        <button
          type="button"
          onClick={onAction}
          style={{
            marginLeft: 'auto',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: 0,
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--accent-ico)',
          }}
        >
          {action} →
        </button>
      )}
    </div>
  );
}

// ── funnel ───────────────────────────────────────────────────────────
export interface FunnelStep {
  label: string;
  n: number;
  hint: string;
  tone: Tone;
}

/** Each step shows its conversion from the previous one — that ratio, not the
    raw count, is what says where fans are lost. */
export function Funnel({ steps }: { steps: FunnelStep[] }) {
  const top = Math.max(...steps.map((x) => x.n), 1);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${steps.length}, 1fr)`, gap: 12 }}>
      {steps.map((s, i) => {
        const prev = i > 0 ? steps[i - 1]?.n : undefined;
        const conv = prev !== undefined ? ratio(s.n, prev) * 100 : null;
        return (
          <div
            key={s.label}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              padding: '16px 16px 14px',
              borderRadius: 'var(--rad-md)',
              background: 'var(--card-2)',
              border: '1px solid var(--line)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: 'var(--muted)' }}>
                {s.label}
              </span>
              {conv !== null && (
                <span style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 11.5, fontWeight: 600, color: 'var(--faint)' }}>
                  {pct(conv)}
                </span>
              )}
            </div>
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 30,
                fontWeight: 800,
                letterSpacing: -0.8,
                lineHeight: 1,
                color: s.n === 0 ? 'var(--faint)' : 'var(--text)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {fmt(s.n)}
            </span>
            <span style={{ height: 8, borderRadius: 5, background: 'color-mix(in srgb, var(--om-ink) 5%, transparent)', overflow: 'hidden', display: 'block' }}>
              <span
                style={{
                  display: 'block',
                  height: '100%',
                  width: Math.max((s.n / top) * 100, s.n ? 3 : 0) + '%',
                  borderRadius: 5,
                  background: toneColor(s.tone),
                  opacity: 0.9,
                }}
              />
            </span>
            <span style={{ fontSize: 12, fontWeight: 550, color: 'var(--faint)', lineHeight: 1.35 }}>{s.hint}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── revenue split ────────────────────────────────────────────────────
export function RevenueSplit({ parts, total }: { parts: { label: string; v: number; color: string }[]; total: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11, position: 'relative' }}>
      <div style={{ display: 'flex', height: 10, borderRadius: 6, overflow: 'hidden', background: 'color-mix(in srgb, var(--om-ink) 5%, transparent)', gap: 2 }}>
        {parts.map((p) => (
          <span key={p.label} style={{ width: (total > 0 ? (p.v / total) * 100 : 0) + '%', background: p.color, display: 'block' }} />
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px' }}>
        {parts.map((p) => (
          <span
            key={p.label}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 600, color: p.v ? 'var(--text)' : 'var(--faint)' }}
          >
            <span style={{ width: 8, height: 8, borderRadius: 3, background: p.v ? p.color : 'var(--faint)', opacity: p.v ? 1 : 0.5 }} />
            {p.label}
            <span style={{ fontFamily: 'var(--mono)', fontWeight: 600, color: p.v ? 'var(--ok-ico)' : 'var(--faint)' }}>{usd(p.v)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
